"use strict";Object.defineProperty(exports, "__esModule", {value: true});/**
 * Lean PostHog event names for the signup/onboarding funnel dashboard.
 *
 * Trigger guide:
 * - onboarding_started: fired once when a signup token opens the setup flow.
 * - onboarding_step_viewed: fired when the visible setup step changes.
 * - onboarding_step_completed: fired when the user successfully advances past a setup step.
 * - signup_completed: server-side source-of-truth event fired once after setup creates a user/org context.
 * - onboarding_completed: legacy frontend event for setup completion; do not use as source-of-truth signup cohort.
 * - app_download_prompt_viewed: fired once when the final "Ready to go" download screen renders.
 * - app_download_clicked: fired when the user clicks a desktop/mobile app download CTA.
 * - app_opened_after_signup: fired once after a signup-attributed user opens/logs into an app.
 * - subscription_started: fired after the frontend sees the post-checkout success redirect.
 * - subscription_converted_to_paid: fired when the frontend starts a paid-upgrade conversion flow.
 * - onboarding_payment_abandoned: fired when a user exits the onboarding payment step without paying
 *   (outcome: skipped / checkout_cancelled on the frontend; expired on the backend).
 * - onboarding_checkout_started: fired once per attempt when the user clicks the trial CTA on the
 *   payment offer, before the Stripe redirect — the intent signal between `payment` step_viewed and
 *   the return-leg outcomes. Carries the offer-estimate properties (seats, patients/month, selected
 *   tier units, estimated cost in cents) so drop-off at Stripe is segmentable by configured plan.
 * - onboarding_skip_dialog_viewed: fired once per attempt when the skip-confirm dialog opens on the
 *   payment offer. Skip-dialog views minus `skipped` outcomes measure how many users the dialog
 *   talks back into the offer.
 * - onboarding_demo_callout_viewed: fired once per attempt when a book-a-demo surface appears on the
 *   payment step — the slider-triggered enterprise callout (`source=enterprise_callout`,
 *   `trigger=seats|patients`) or the over-cutoff contact-us branch (`source=enterprise_branch`,
 *   `trigger=survey_volume`).
 * - onboarding_demo_clicked: fired when the user clicks a book-a-demo / contact-our-team CTA.
 *   Payment uses the callout source values above; the dedicated step uses `source=demo_step`.
 * - onboarding_demo_booked: fired once per organization when the embedded Calendly widget reports
 *   a scheduled event. Carries `{ organization_id, source: 'demo_step' }` only — never invitee details.
 * - onboarding_step_errored: fired when a funnel-blocking request fails and the user sees an error
 *   surface (`error_kind`: pricing_load / checkout_start / survey_load / subscription_settle /
 *   demo_embed_load / demo_scheduling_gone / demo_scheduling_lookup — the demo_* kinds also carry
 *   a Sentry report so a broken Calendly event alarms ops instead of silently skipping the step).
 *   Carries a best-effort `sentry_event_id`. Emitted per user-visible failure (a manual
 *   retry that fails again re-emits) — never per quiet retry.
 * - onboarding_phone_skipped: fired when a user leaves the onboarding phone-number step via the
 *   persistent-failure escape hatch ("pick your number in Settings"), so a stuck entitlement can
 *   never hard-strand signup. Named like the `onboarding_payment_abandoned` precedent — a distinct
 *   step-exit-without-completion event rather than an outcome property on step_completed.
 * - onboarding_invites_sent: fired once when the coworker-invite step advances (auto-advance after
 *   all rows succeed, "Continue anyway" past failures, or an initial skip). Carries
 *   `{ organization_id, invite_count }` only — the count of successful invite operations, never any
 *   invitee name or email address.
 * - onboarding_first_action_completed: fired once when the guided first action succeeds (the user
 *   sends the self-SMS test or the first teammate message). Carries `{ organization_id, action_type }`
 *   only (`action_type` = `self_sms` | `team_chat`) — never a phone number, recipient, or message body.
 * - onboarding_first_action_dismissed: fired once when the user skips the guided first action via the
 *   "take me to my inbox" link. Same `{ organization_id, action_type }` allowlist as completed.
 * - sms_trial_entry_clicked: fired when a declined-SMS-cohort user clicks a "start your SMS trial"
 *   entry point. Carries `{ organization_id, source }` only (`source` = `finish_surface` | `banner`).
 *
 * Keep this list intentionally small. Add properties for segmentation instead of adding
 * one-off event names, and never include PHI/customer content in event names.
 */
 const ONBOARDING_EVENTS = {
  STARTED: 'onboarding_started',
  SIGNUP_COMPLETED: 'signup_completed',
  STEP_VIEWED: 'onboarding_step_viewed',
  STEP_COMPLETED: 'onboarding_step_completed',
  COMPLETED: 'onboarding_completed',
  APP_DOWNLOAD_PROMPT_VIEWED: 'app_download_prompt_viewed',
  APP_DOWNLOAD_CLICKED: 'app_download_clicked',
  APP_OPENED_AFTER_SIGNUP: 'app_opened_after_signup',
  SUBSCRIPTION_STARTED: 'subscription_started',
  SUBSCRIPTION_CONVERTED_TO_PAID: 'subscription_converted_to_paid',
  PAYMENT_ABANDONED: 'onboarding_payment_abandoned',
  CHECKOUT_STARTED: 'onboarding_checkout_started',
  SKIP_DIALOG_VIEWED: 'onboarding_skip_dialog_viewed',
  DEMO_CALLOUT_VIEWED: 'onboarding_demo_callout_viewed',
  DEMO_CLICKED: 'onboarding_demo_clicked',
  DEMO_BOOKED: 'onboarding_demo_booked',
  STEP_ERRORED: 'onboarding_step_errored',
  PHONE_SKIPPED: 'onboarding_phone_skipped',
  INVITES_SENT: 'onboarding_invites_sent',
  FIRST_ACTION_COMPLETED: 'onboarding_first_action_completed',
  FIRST_ACTION_DISMISSED: 'onboarding_first_action_dismissed',
  SMS_TRIAL_ENTRY_CLICKED: 'sms_trial_entry_clicked',
} 

; exports.ONBOARDING_EVENTS = ONBOARDING_EVENTS
